import mixpanel from "mixpanel-browser";

const TOKEN = "00b8154ab47aea66794d7b85517091c7";
const API = "https://api.mixpanel.com";

let initialized = false;
let distinctId = null;
let superProperties = {};
let sdkReady = false;
let recording = false;

// Send events directly — bypasses the v2 SDK track() which has a bug
const sendEvent = async (event, properties = {}) => {
  try {
    const data = btoa(JSON.stringify([{
      event,
      properties: {
        token: TOKEN,
        distinct_id: distinctId || "anonymous",
        time: Math.floor(Date.now() / 1000),
        ...superProperties,
        ...properties,
      },
    }]));
    await fetch(`${API}/track?data=${encodeURIComponent(data)}`);
  } catch (e) {
    // fail silently
  }
};

// Percentage of browser tabs that get a Session Replay. Decided once per tab
// (sessionStorage) so a sampled tab keeps recording across navigations.
export const REPLAY_SAMPLE_PERCENT = 25;
const REPLAY_SAMPLE_KEY = "atrium_replay_sampled";

// mixpanel-browser 2.76 Session Replay options. The SDK is used for replay only;
// events are sent by sendEvent() above.
export const REPLAY_CONFIG = {
  autocapture: false,
  track_pageview: false,
  // 0 stops the SDK from starting a recording on init. trackPageView() applies
  // the sample rate and route exclusions, then starts recording explicitly.
  record_sessions_percent: 0,
  // Replace every text node and input value with placeholders. Do not set
  // record_mask_text_selector: on its own it switches mask-all-text off.
  record_mask_all_text: true,
  record_mask_all_inputs: true,
  // Never capture images or media (avatars, child photos, uploads).
  record_block_selector: "img, picture, video, audio, iframe, object, embed, svg image",
  record_canvas: false,
  record_console: false,
  record_network: false,
  record_heatmap_data: false,
};

// Public pages (members, visitors, survey respondents) and sign-in: never recorded.
const PUBLIC_ROUTE_PREFIXES = [
  "/survey",
  "/link-hub",
  "/connect",
  "/staff-intake",
  "/volunteer-intake",
  "/staff-pulse",
  "/volunteer-pulse",
  "/team-checkin",
  "/demo",
  "/auth",
  "/member",
];

// Admin pages showing individual people, responses, prayer requests or
// locations: never recorded.
const SENSITIVE_ROUTES = [
  /^\/admin\/people(\/|$)/,
  /^\/admin\/connect\/prayers(\/|$)/,
  /^\/admin\/connect\/visitors(\/|$)/,
  // Heartbeat campaign details (individual responses), edit and action items;
  // only the campaign list and the new-campaign form are recorded.
  /^\/admin\/heartbeat\/(?!new\/?$)[^/]+/,
  /^\/admin\/settings\/profile(\/|$)/,
  /^\/admin\/member-location(\/|$)/,
  /^\/admin\/insights(\/|$)/,
  /^\/admin\/team\/members\/[^/]+/,
  /^\/admin\/staff\/people\/[^/]+/,
  /^\/admin\/volunteers\/people\/[^/]+/,
];

const isPublicRoute = (path) =>
  PUBLIC_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

const isSensitiveRoute = (path) =>
  SENSITIVE_ROUTES.some((pattern) => pattern.test(path));

export const isReplayExcludedRoute = (path) =>
  isPublicRoute(path) || isSensitiveRoute(path);

const isReplaySampled = () => {
  try {
    let sampled = sessionStorage.getItem(REPLAY_SAMPLE_KEY);
    if (sampled === null) {
      sampled = Math.random() * 100 < REPLAY_SAMPLE_PERCENT ? "1" : "0";
      sessionStorage.setItem(REPLAY_SAMPLE_KEY, sampled);
    }
    return sampled === "1";
  } catch (e) {
    return false;
  }
};

// The SDK is initialised lazily, on the first recorded page, so it never runs
// (or resumes a recording) while an excluded route is on screen.
const startReplay = () => {
  if (recording) return;
  if (!sdkReady) {
    mixpanel.init(TOKEN, REPLAY_CONFIG);
    sdkReady = true;
    if (distinctId) mixpanel.identify(distinctId);
  }
  recording = true;
  mixpanel.start_session_recording();
};

// Unconditional once the SDK runs: mixpanel.reset() can resume a recording
// behind our back, so the local flag alone is not proof that nothing records.
const stopReplay = () => {
  if (!sdkReady) return;
  recording = false;
  mixpanel.stop_session_recording();
};

const normalizePath = (path) =>
  path.replace(/\/\d+/g, "/:id");

export const initMixpanel = () => {
  if (initialized) return;
  initialized = true;

  sendEvent("App Loaded");

  // Re-identify on page load if user is already logged in
  try {
    const stored = localStorage.getItem("user");
    if (stored) {
      const u = JSON.parse(stored);
      if (u?.id) {
        identifyUser({
          id: u.id,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          phone: u.phone,
          role: u.role,
          organizationId: u.organizationId || u.churchId,
          orgName: u.organizationName || u.churchName,
          campusId: u.campus,
          status: u.status,
          createdAt: u.createdAt,
        });
      }
    }
  } catch (e) {
    // ignore
  }
};

export const identifyUser = ({ id, email, firstName, lastName, phone, role, organizationId, orgName, campusId, status, createdAt }) => {
  if (!id) return;
  distinctId = String(id);

  // Link the replay to the user once the SDK is running
  if (sdkReady) mixpanel.identify(distinctId);

  const peopleData = {
    // Mixpanel reserved properties
    $name: [firstName, lastName].filter(Boolean).join(" ") || undefined,
    $email: email || undefined,
    $phone: phone || undefined,
    $created: createdAt || undefined,
    // Custom properties
    role,
    org_id: organizationId,
    org_name: orgName,
    campus_id: campusId || undefined,
    status: status || undefined,
    first_name: firstName || undefined,
    last_name: lastName || undefined,
  };

  // Remove undefined keys
  Object.keys(peopleData).forEach(k => peopleData[k] === undefined && delete peopleData[k]);

  // Set super-properties so every subsequent event carries user context
  superProperties = {
    user_id: distinctId,
    role,
    org_id: organizationId,
    org_name: orgName,
  };
  if (campusId) superProperties.campus_id = campusId;

  // Send people data directly
  fetch(`${API}/engage`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `data=${encodeURIComponent(btoa(JSON.stringify({
      $token: TOKEN,
      $distinct_id: distinctId,
      $set: peopleData,
    })))}`,
  }).catch(() => {});
};

export const resetUser = () => {
  distinctId = null;
  superProperties = {};
  recording = false;
  if (sdkReady) mixpanel.reset();
};

export const trackPageView = (path) => {
  if (isReplayExcludedRoute(path)) {
    stopReplay();
  } else if (isReplaySampled()) {
    startReplay();
  }
  sendEvent("Page View", { path: normalizePath(path) });
};

export const track = (event, properties = {}) => {
  sendEvent(event, properties);
};
